import { useEffect, useState } from 'react';

import { dialogService, toastService } from '@thefarmersfront/kitchen-mobile';

import { CAMERA_RESULT_ERROR_CODE, DIALOG_MESSAGES, DIALOG_TITLES, TOAST_MESSAGES } from '~/constants';

/**
 * 브릿지를 통해 카메라 또는 갤러리에 접근하는 커스텀훅
 * @remarks 사진 촬영 또는 이미지를 선택하는 이미지 결과를 반환
 */
export const useMoveToCameraOrGallery = () => {
  const [appImageResult, setAppImageResult] = useState<{ data: string; type: string; extension: string } | null>(null);

  const requestAppCamera = ({
    headerTitle = '',
    messageTitle = '',
    messageSubtitle = '',
    duration = 5000,
  }: {
    headerTitle: string;
    messageTitle?: string;
    messageSubtitle?: string;
    duration?: number;
  }) => {
    const params = JSON.stringify({
      type: 'camera',
      title: headerTitle,
      messageTitle,
      messageSubtitle,
      duration,
    });

    try {
      if (window.JSBridge) {
        window.JSBridge.moveToCapture(params);
      } else if (window.webkit) {
        window.webkit.messageHandlers.moveToCapture.postMessage(params);
      }
    } catch (err) {
      console.error('>> 카메라 로드 중 실패 :: ', err);
      toastService.addToast({ message: TOAST_MESSAGES.FAILED_LOAD_CAMERA, color: 'error' });
    }
  };

  const requestAppGallery = () => {
    try {
      if (window.JSBridge) {
        window.JSBridge.moveToGallery();
      } else if (window.webkit) {
        window.webkit.messageHandlers.moveToGallery.postMessage(null);
      }
    } catch (err) {
      console.error('>> 사진첩 로드 중 실패 :: ', err);
      toastService.addToast({ message: TOAST_MESSAGES.FAILED_LOAD_GALLERY, color: 'error' });
    }
  };

  const uploadFileOnDevMode = async (
    type: 'selectImage' | 'capture'
  ): Promise<{ data: string; type: string; extension: string } | null> => {
    return new Promise((resolve) => {
      const fileInput = document.createElement('input');

      fileInput.type = 'file';
      fileInput.accept = 'image/*';
      fileInput.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) {
          resolve(null);
          return;
        }

        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);
        fileReader.onload = () => {
          const data = (fileReader.result as string).split(',')?.[1] ?? '';
          resolve({ data, type, extension: file.name });
        };
        fileReader.onerror = () => {
          resolve(null);
        };
      };

      fileInput.click();
    });
  };

  useEffect(() => {
    window.onImageResult = (data: string, type: string, extension: string) => {
      if (CAMERA_RESULT_ERROR_CODE.IMAGE_SIZE_LARGE_ERROR === data) {
        dialogService.addDialog({
          open: true,
          title: DIALOG_TITLES.FAILED_UPLOAD_IMAGE_SIZE_LARGE,
          children: DIALOG_MESSAGES.FAILED_UPLOAD_IMAGE_SIZE_LARGE,
          onConfirm: () => {
            setAppImageResult(null);
          },
        });
        return;
      }

      if (CAMERA_RESULT_ERROR_CODE.IMAGE_LOAD_ERROR === data) {
        dialogService.addDialog({
          open: true,
          title: DIALOG_TITLES.FAILED_UPLOAD_IMAGE_LOAD_ERROR,
          children: DIALOG_MESSAGES.FAILED_UPLOAD_IMAGE_LOAD_ERROR,
          onConfirm: () => {
            setAppImageResult(null);
          },
        });
        return;
      }

      setAppImageResult({ data, type, extension });
    };
    return () => {
      window.onImageResult = null;
      setAppImageResult(null);
    };
  }, []);

  return {
    requestAppCamera,
    requestAppGallery,
    appImageResult,
    uploadFileOnDevMode,
  };
};

export default useMoveToCameraOrGallery;
